import React, { createContext, useContext, useMemo, useState } from 'react'

interface PeerContextType {
    peer: RTCPeerConnection | null;
    createOffer: () => Promise<RTCSessionDescriptionInit>;
    createAnswer: (offer: any) => Promise<RTCSessionDescriptionInit>;
    remoteAnswer: (ans: any) => void;
    remoteStream: MediaStream | null,
    setRemoteStream: (stream: any) => void
}

const PeerContext = createContext<PeerContextType | null>(null);


export const usePeer = () => {
    const context = useContext(PeerContext);
    if (!context) {
        throw new Error('usePeer must be used within a PeerProvider');
    }
    return context;
};

const WEBRTC_UNAVAILABLE = 'WebRTC is unavailable in this environment'

export const PeerProvider = (props: any) => {
    const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null)
    // Construct lazily and tolerate failures so the entire app doesn't crash-loop
    // when the browser disallows RTCPeerConnection (extensions, policies, headless envs).
    // Consumers that actually use WebRTC must guard on `peer` being null.
    const peer = useMemo<RTCPeerConnection | null>(() => {
        if (typeof window === 'undefined' || typeof RTCPeerConnection === 'undefined') {
            return null
        }
        try {
            return new RTCPeerConnection({
                iceServers: [
                    {
                        urls: [
                            "stun:stun.l.google.com:19302",
                            "stun:global.stun.twilio.com:3478",
                        ],
                    }
                ]
            })
        } catch (err) {
            console.warn('PeerProvider: RTCPeerConnection construction failed; WebRTC features disabled.', err)
            return null
        }
    }, [])

    const createOffer = async () => {
        if (!peer) throw new Error(WEBRTC_UNAVAILABLE)
        const offer = await peer.createOffer()
        await peer.setLocalDescription(new RTCSessionDescription(offer))
        return offer
    }

    const createAnswer = async (offer: RTCSessionDescriptionInit) => {
        if (!peer) throw new Error(WEBRTC_UNAVAILABLE)
        if (!offer) {
            throw new Error('Invalid offer');
        }
        await peer.setRemoteDescription(offer)
        const answer = await peer.createAnswer()
        await peer.setLocalDescription(new RTCSessionDescription(answer))
        return answer

    }


    const remoteAnswer = async (ans: RTCSessionDescriptionInit) => {
        if (!peer) throw new Error(WEBRTC_UNAVAILABLE)
        await peer.setRemoteDescription(new RTCSessionDescription(ans))
    }

    return (
        <PeerContext.Provider value={{ peer, createOffer, createAnswer, remoteAnswer,remoteStream,setRemoteStream}}>{props.children}</PeerContext.Provider>
    )
}


